
import React, { useState, useEffect } from 'react';
import { ArrowRight, Sparkles, Key, X, AlertCircle, ChevronLeft } from 'lucide-react';
import { joinProjectByToken } from '../services/databaseService';
import { auth } from '../services/firebase';
import { BrandLogo } from './BrandLogo';

interface WelcomeProps {
  onNext: () => void;
  onProjectJoined: () => void;
  onBack: () => void;
  initialInviteCode?: string | null;
}

// --- COMO ALTERAR A IMAGEM ---
// 1. Se sua imagem estiver na internet: Cole o link direto (terminado em .jpg/.png) abaixo.
// 2. Se for um arquivo local: Coloque o arquivo na pasta 'public' do projeto e use o caminho (ex: "/minha-foto.jpg")
const DEFAULT_WELCOME_IMAGE = "https://i.postimg.cc/DzXgmzX1/Gemini-Generated-Image-dw24umdw24umdw24.png";

export const Welcome: React.FC<WelcomeProps> = ({ onNext, onProjectJoined, onBack, initialInviteCode }) => {
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Auto-open modal if invite code is present
  useEffect(() => {
    if (initialInviteCode) {
      setInviteCode(initialInviteCode);
      setShowJoinModal(true);
    }
  }, [initialInviteCode]);

  const handleJoin = async () => {
    if (inviteCode.length < 4) return;
    setLoading(true);
    setError("");
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Usuário não autenticado");
      
      const projectId = await joinProjectByToken(user.uid, inviteCode);
      if (projectId) {
        onProjectJoined(); // Parent updates view, component unmounts
      } else {
        setError("Código inválido. Verifique com o gestor do projeto.");
        setLoading(false);
      }
    } catch (err) {
      setError("Erro ao tentar entrar na obra.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen mesh-gradient flex flex-col px-8 pt-16 pb-10 animate-fadeIn overflow-y-auto relative">
      {/* Botão Voltar */}
      <button 
        onClick={onBack} 
        className="absolute top-6 left-6 p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm rounded-full shadow-sm border border-slate-100 dark:border-slate-800 hover:scale-105 transition-transform z-20"
      >
        <ChevronLeft size={20} className="text-slate-600 dark:text-slate-400" />
      </button>

      <div className="flex-1 flex flex-col items-center">
        {/* Logo na Tela Inicial */}
        <div className="mb-8">
           <BrandLogo size="lg" variant="full" />
        </div>

        <div className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-100 dark:bg-emerald-800/40 rounded-full text-[10px] font-black text-emerald-800 dark:text-emerald-400 uppercase tracking-widest mb-8 shadow-sm">
          <Sparkles size={12} className="animate-pulse" /> Gestão Profissional de Obras
        </div>

        <div className="relative w-full aspect-square mb-10 group max-w-[320px]">
          <div className="absolute inset-0 bg-emerald-200/40 dark:bg-emerald-700/20 rounded-[4rem] blur-2xl group-hover:blur-3xl transition-all duration-700"></div>
          
          <div className="relative h-full w-full rounded-[4rem] overflow-hidden border-4 border-white dark:border-slate-800 shadow-2xl transition-transform duration-500 hover:scale-[1.03] group-card">
            <img 
              src={DEFAULT_WELCOME_IMAGE} 
              alt="Família acompanhando a obra" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/60 to-transparent pointer-events-none"></div>
          </div>
        </div>

        <div className="text-center space-y-4 mb-8 max-w-[300px]">
          <h1 className="text-4xl font-black text-emerald-900 dark:text-white tracking-tighter leading-none">
            Seja <br/><span className="text-emerald-600">Bem-vindo!</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm font-bold leading-relaxed">
            Elimine o caos das planilhas. Comece a gerenciar sua construção com eficiência absoluta.
          </p>
        </div>
      </div>

      <div className="mt-auto space-y-4">
        <button 
          onClick={onNext}
          className="w-full bg-emerald-800 dark:bg-emerald-600 text-white py-6 rounded-[2.5rem] font-black text-xs uppercase tracking-widest shadow-2xl flex items-center justify-center gap-4 active:scale-95 transition-all animate-pulsar-premium"
        >
          Novo Projeto
          <ArrowRight size={20} />
        </button>
        
        <button 
          onClick={() => setShowJoinModal(true)}
          className="w-full bg-white dark:bg-slate-900 text-emerald-800 dark:text-emerald-400 py-6 rounded-[2.5rem] font-black text-xs uppercase tracking-widest border-2 border-emerald-100 dark:border-slate-800 flex items-center justify-center gap-4 active:scale-95 transition-all shadow-sm"
        >
          <Key size={20} />
          Acessar via Código
        </button>
      </div>

      {showJoinModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-950 w-full max-w-sm rounded-[3.5rem] p-10 shadow-2xl relative overflow-hidden">
             <button onClick={() => setShowJoinModal(false)} className="absolute top-8 right-8 text-slate-400 hover:text-red-500"><X size={24}/></button>
             
             <div className="text-center mb-10">
                <div className="size-20 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                   <Key size={38} />
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Código de Acesso</h3>
                <p className="text-[11px] font-bold text-slate-400 mt-3 uppercase tracking-widest">Insira o código compartilhado pelo gestor do projeto.</p>
             </div>

             <div className="space-y-8">
                <input 
                  type="text"
                  placeholder="EX: ABC123"
                  className="w-full bg-stone-100 dark:bg-slate-900 border-none rounded-3xl py-6 px-8 text-center text-3xl font-black uppercase tracking-[0.4em] outline-none focus:ring-4 focus:ring-emerald-500 text-emerald-900 dark:text-white"
                  value={inviteCode}
                  onChange={e => setInviteCode(e.target.value)}
                  maxLength={10}
                />

                {error && (
                  <p className="flex items-center justify-center gap-3 text-red-500 text-[10px] font-black uppercase tracking-widest animate-shake">
                    <AlertCircle size={16} /> {error}
                  </p>
                )}

                <button 
                  onClick={handleJoin}
                  disabled={loading || inviteCode.length < 4}
                  className="w-full bg-emerald-800 dark:bg-emerald-600 text-white py-5 rounded-3xl font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-4 disabled:opacity-40 transition-all"
                >
                  {loading ? 'Validando...' : 'Entrar no Projeto'}
                  {!loading && <ArrowRight size={20} />}
                </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};
